// import { clientEnv, nodeEnvironment } from "@/config/clientEnv";
import { NodeEnvironment } from "@/domains/common/types";
import {
  DATADOG_LOGGING_CLIENT_TOKEN,
  DATADOG_SITE,
  DATADOG_WEB_SERVICE_NAME,
} from "@/domains/datadog/constants";
import { getBackendAnalyticsBaseUrl } from "@/manager/backend-api-client/initializeBackendApiManager";
import { clientEnvModule } from "@/modules/client-env";
import { datadogLogs } from "@datadog/browser-logs";

/**
 * Matches the site name of our datadog instance.
 */

const LOW_VALUE_BROWSER_LOG_PATTERNS = [
  /^\[metrics\] /,
  /^Fetch error GET https:\/\/(?:api\.mem\.(?:ai|run)|backend-prod\.mem\.engineering)\/api\/v2\/health(?:\?.*)?$/,
  /^Fetch error (GET|POST) https:\/\/(api|cdn)\.segment\.(io|com)\//,
  /^Error sending segment performance metrics TypeError: Failed to fetch$/,
  /^Failed to create PluginFactory [\s\S]*"Google Analytics 4 Web"/,
  /^Fetch error POST https:\/\/(?:analytics\.google\.com|www\.google\.com|www\.googleadservices\.com|googleads\.g\.doubleclick\.net)\//,
  /^(Fetch|XHR) error (GET|POST) https:\/\/[^/]+\.pusher\.com\//,
  /^(?:Uncaught )?"?ResizeObserver loop (?:completed with undelivered notifications\.|limit exceeded)"?$/,
];

const shouldForwardDatadogBrowserLog = (event: { message?: unknown }) => {
  const message = typeof event.message === "string" ? event.message : "";
  return !LOW_VALUE_BROWSER_LOG_PATTERNS.some(pattern => pattern.test(message));
};

export const initializeDatadogLoggingClient = async () => {
  const nodeEnv = clientEnvModule.nodeEnv();
  const packageVersion = clientEnvModule.packageVersion();
  const { buildInfo } = clientEnvModule.buildInfo();
  const version = `${packageVersion}-${buildInfo.gitCommitSha}`;
  const platform = clientEnvModule.isDesktop() ? "desktop" : "web";
  const operatingSystem = navigator.userAgent;

  datadogLogs.init({
    clientToken: DATADOG_LOGGING_CLIENT_TOKEN,
    site: DATADOG_SITE,
    version,
    forwardErrorsToLogs: true,
    service: DATADOG_WEB_SERVICE_NAME,
    sessionSampleRate: 100,
    env: nodeEnv,
    proxy: `${getBackendAnalyticsBaseUrl()}/analytics/datadog/proxy`,
    beforeSend: shouldForwardDatadogBrowserLog,
  });

  datadogLogs.setGlobalContextProperty("platform", platform);
  datadogLogs.setGlobalContextProperty("app_version", packageVersion);
  datadogLogs.setGlobalContextProperty("operating_system", operatingSystem);

  /**
   * If we aren't in production, we default to using the console
   * logger instead of the real logger.
   */
  if (nodeEnv !== NodeEnvironment.Production) {
    datadogLogs.logger.setHandler("console");
  }

  return datadogLogs;
};
